import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, CheckCircle, XCircle, RefreshCw, Wifi, Zap, Globe } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Live VPN tunnel diagnostic panel for the Scan module.
 *
 * Polls GET /vpn/tunnel-state every 5s while `active`, and offers a
 * "Tester le VPN" button wired to POST /vpn/tunnel-test. Backend handles
 * interface detection dynamically (tun*, wg*, utun*, ppp*); this component
 * only renders what it receives.
 */
export default function VpnTunnelStatus({ active = true, onChange }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get('/vpn/tunnel-state');
      setState(data);
      if (onChange) onChange(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur de lecture VPN');
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  const runTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post('/vpn/tunnel-test');
      setTestResult(data);
    } catch (err) {
      setTestResult({
        ok: false,
        error: err?.response?.data?.detail || err.message || 'Test échoué',
      });
    } finally {
      setTesting(false);
      fetchState();
    }
  }, [fetchState]);

  useEffect(() => {
    if (!active) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    fetchState();
    pollRef.current = setInterval(fetchState, 5000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [active, fetchState]);

  const connected = Boolean(state?.connected);
  const badgeColor = connected
    ? 'bg-green-500/10 text-green-400 border-green-500/30'
    : 'bg-red-500/10 text-red-400 border-red-500/30';
  const Icon = connected ? CheckCircle : XCircle;

  return (
    <div className="mt-4 p-4 bg-brand-black rounded-lg border border-gray-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-400" />
          <h4 className="text-sm font-medium text-white">État du tunnel VPN</h4>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${badgeColor}`}>
            <Icon className="w-3 h-3" />
            {connected ? 'Connecté' : 'Déconnecté'}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchState}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-brand-black-light border border-gray-700 hover:border-gray-500 text-gray-300 rounded-md transition-colors text-xs disabled:opacity-50"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium disabled:opacity-50"
          >
            <Zap className={`w-3.5 h-3.5 ${testing ? 'animate-pulse' : ''}`} />
            {testing ? 'Test en cours…' : 'Tester le VPN'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Field icon={Wifi} label="Interface" value={state?.interface || '—'} mono />
        <Field icon={Globe} label="IP locale du tunnel" value={state?.tunnel_ip || '—'} mono />
        <Field icon={Globe} label="IP publique (VPN)" value={state?.vpn_public_ip || '—'} mono
               hint={state?.vpn_is_default_route ? 'Route par défaut via VPN' : undefined} />
        <Field icon={Globe} label="IP WAN (direct)" value={state?.wan_public_ip || '—'} mono />
      </div>

      {state && !connected && state.candidates?.length > 0 && (
        <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-300">
          Interface détectée ({state.candidates.join(', ')}) mais le trafic ne sort pas via le tunnel.
          Vérifiez les routes ou relancez le VPN.
        </div>
      )}

      {testResult && (
        <div className={`mt-3 p-3 rounded-lg border text-xs ${
          testResult.ok
            ? 'bg-green-500/10 border-green-500/30 text-green-300'
            : 'bg-red-500/10 border-red-500/30 text-red-300'
        }`}>
          <div className="flex items-center gap-2 font-medium mb-1">
            {testResult.ok ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {testResult.ok ? 'Test réussi' : 'Test échoué'}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-gray-300">
            {testResult.interface && <span><b>Interface:</b> {testResult.interface}</span>}
            {testResult.ping_target && <span><b>Cible ping:</b> {testResult.ping_target}</span>}
            {testResult.latency_ms !== null && testResult.latency_ms !== undefined && (
              <span><b>Latence:</b> {testResult.latency_ms} ms</span>
            )}
            {testResult.public_ip && <span><b>IP publique:</b> {testResult.public_ip}</span>}
            {testResult.reason === 'no_vpn_interface' && (
              <span className="col-span-full">Aucune interface VPN active (tun*, wg*, utun*, ppp*).</span>
            )}
            {testResult.error && <span className="col-span-full">{testResult.error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value, mono, hint }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-gray-500 uppercase tracking-wide text-[10px] flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </span>
      <span className={`text-white ${mono ? 'font-mono' : ''}`}>{value}</span>
      {hint && <span className="text-[10px] text-gray-500">{hint}</span>}
    </div>
  );
}
